import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Plus, Trash2, ChevronUp, ChevronDown, Type } from 'lucide-react';
import type { Page, Component } from '../ComponentEditModal.types';
import { RichTextEditor } from '../../RichTextEditor';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';
import { ButtonsEditor } from '../shared/ButtonsEditor';
import type { ButtonData } from '../shared/ButtonsEditor';

interface TextBlock {
  id: string;
  title?: string;
  content: string;
  order: number;
}

interface TextBlocksData {
  componentTitle?: string;
  componentSubtitle?: string;
  componentDescription?: string;
  layout?: string;
  columns?: string;
  headerAlignment?: string;
  globalAlignment?: string;
  gap?: string;
  horizontalPadding?: string;
  blocks?: TextBlock[];
  backgroundColor?: string;
  customColors?: {
    background?: string;
    title?: string;
    text?: string;
    border?: string;
  };
  buttons?: ButtonData[];
}

interface TextBlocksEditorProps {
  formData: TextBlocksData;
  setFormData: (data: TextBlocksData) => void;
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
}

export const TextBlocksEditor: React.FC<TextBlocksEditorProps> = ({
  formData,
  setFormData,
  pages,
  isLoadingPages,
  currentPageComponents,
}) => {
  const data = formData;
  const buttons = data.buttons || [];
  const themeColors = useCustomColors();

  const addBlock = () => {
    const newBlock: TextBlock = {
      id: uuidv4(),
      title: '',
      content: '',
      order: (data.blocks || []).length,
    };
    setFormData({ ...data, blocks: [...(data.blocks || []), newBlock] });
  };

  const updateBlock = (index: number, updates: Partial<TextBlock>) => {
    const newBlocks = [...(data.blocks || [])];
    newBlocks[index] = { ...newBlocks[index], ...updates };
    setFormData({ ...data, blocks: newBlocks });
  };

  const removeBlock = (index: number) => {
    const newBlocks = (data.blocks || []).filter((_, i) => i !== index);
    setFormData({ ...data, blocks: newBlocks });
  };

  const moveBlock = (index: number, direction: 'up' | 'down') => {
    const newBlocks = [...(data.blocks || [])];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex >= 0 && targetIndex < newBlocks.length) {
      [newBlocks[index], newBlocks[targetIndex]] = [newBlocks[targetIndex], newBlocks[index]];
      newBlocks.forEach((block, i) => {
        block.order = i;
      });
      setFormData({ ...data, blocks: newBlocks });
    }
  };

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: { ...data.customColors, [key]: value },
    });
  };

  // Get theme-aware color defaults
  const colorFields = getColorFieldsForComponent('text-blocks', themeColors);

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <HeaderSection data={data} setFormData={setFormData} />

      {/* Layout Settings */}
      <LayoutSection data={data} setFormData={setFormData} />

      {/* Text Blocks */}
      <BlocksSection
        blocks={data.blocks || []}
        onAddBlock={addBlock}
        onUpdateBlock={updateBlock}
        onRemoveBlock={removeBlock}
        onMoveBlock={moveBlock}
      />

      {/* Call-to-Action Buttons */}
      <ButtonsEditor
        buttons={buttons}
        onChange={newButtons => setFormData({ ...data, buttons: newButtons })}
        pages={pages}
        isLoadingPages={isLoadingPages}
        currentPageComponents={currentPageComponents}
        label="Call-to-Action Buttons (Optional)"
      />

      {/* Color Customization */}
      <ColorCustomizationPanel
        title="Colors (Optional)"
        description="Customize text blocks colors. Leave empty to use defaults."
        customColors={data.customColors as Record<string, string | undefined>}
        colorFields={colorFields}
        onUpdateColor={updateCustomColor}
        onClearAllColors={() => setFormData({ ...data, customColors: undefined })}
      />
    </div>
  );
};

// Sub-components

interface HeaderSectionProps {
  data: TextBlocksData;
  setFormData: (data: TextBlocksData) => void;
}

const HeaderSection: React.FC<HeaderSectionProps> = ({ data, setFormData }) => (
  <div>
    <h3 className="text-lg font-medium text-gray-900 mb-4">Header Content</h3>
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
        <input
          type="text"
          value={data.componentSubtitle || ''}
          onChange={e => setFormData({ ...data, componentSubtitle: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Enter section subtitle"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
        <input
          type="text"
          value={data.componentTitle || ''}
          onChange={e => setFormData({ ...data, componentTitle: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Enter section title"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <textarea
          value={data.componentDescription || ''}
          onChange={e => setFormData({ ...data, componentDescription: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          rows={3}
          placeholder="Enter section description"
        />
      </div>
    </div>
  </div>
);

interface LayoutSectionProps {
  data: TextBlocksData;
  setFormData: (data: TextBlocksData) => void;
}

const LayoutSection: React.FC<LayoutSectionProps> = ({ data, setFormData }) => (
  <div>
    <h3 className="text-lg font-medium text-gray-900 mb-4">Layout & Style</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Layout Style</label>
        <select
          value={data.layout || 'vertical'}
          onChange={e => setFormData({ ...data, layout: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="vertical">Vertical (Single Column)</option>
          <option value="grid">Grid (Multi-Column)</option>
          <option value="masonry">Masonry (Newspaper Style)</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Columns (Grid/Masonry only)
        </label>
        <select
          value={data.columns || '2'}
          onChange={e => setFormData({ ...data, columns: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          disabled={!['grid', 'masonry'].includes(data.layout || '')}
        >
          <option value="1">1 Column</option>
          <option value="2">2 Columns</option>
          <option value="3">3 Columns</option>
          <option value="4">4 Columns</option>
          <option value="5">5 Columns</option>
          <option value="6">6 Columns</option>
        </select>
      </div>

      <div>
        <label htmlFor="header-alignment" className="block text-sm font-medium text-gray-700 mb-2">
          Header Alignment
        </label>
        <select
          id="header-alignment"
          value={data.headerAlignment || 'center'}
          onChange={e => setFormData({ ...data, headerAlignment: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
          <option value="justify">Justify</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">Title, subtitle, and description</p>
      </div>

      <div>
        <label htmlFor="blocks-alignment" className="block text-sm font-medium text-gray-700 mb-2">
          Blocks Alignment
        </label>
        <select
          id="blocks-alignment"
          value={data.globalAlignment || 'left'}
          onChange={e => setFormData({ ...data, globalAlignment: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
          <option value="justify">Justify</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">Text block content</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Block Spacing</label>
        <select
          value={data.gap || 'md'}
          onChange={e => setFormData({ ...data, gap: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="none">None</option>
          <option value="xs">Extra Small</option>
          <option value="sm">Small</option>
          <option value="md">Normal</option>
          <option value="lg">Large</option>
          <option value="xl">Extra Large</option>
          <option value="2xl">2X Large</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">Space between blocks</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Side Padding</label>
        <select
          value={data.horizontalPadding || 'md'}
          onChange={e => setFormData({ ...data, horizontalPadding: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="none">None</option>
          <option value="xs">Extra Small</option>
          <option value="sm">Small</option>
          <option value="md">Normal</option>
          <option value="lg">Large</option>
          <option value="xl">Extra Large</option>
          <option value="2xl">2X Large</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">Left and right padding</p>
      </div>
    </div>
  </div>
);

interface BlocksSectionProps {
  blocks: TextBlock[];
  onAddBlock: () => void;
  onUpdateBlock: (index: number, updates: Partial<TextBlock>) => void;
  onRemoveBlock: (index: number) => void;
  onMoveBlock: (index: number, direction: 'up' | 'down') => void;
}

const BlocksSection: React.FC<BlocksSectionProps> = ({
  blocks,
  onAddBlock,
  onUpdateBlock,
  onRemoveBlock,
  onMoveBlock,
}) => (
  <div>
    <div className="flex justify-between items-center mb-4">
      <h3 className="text-lg font-medium text-gray-900">Text Blocks</h3>
      <button
        type="button"
        onClick={onAddBlock}
        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-primary-600 bg-primary-50 hover:bg-primary-100"
      >
        <Plus size={16} className="mr-1" />
        Add Block
      </button>
    </div>

    {blocks.length === 0 ? (
      <EmptyBlocksPlaceholder onAddBlock={onAddBlock} />
    ) : (
      blocks.map((block, index) => (
        <BlockItem
          key={block.id || index}
          block={block}
          index={index}
          totalBlocks={blocks.length}
          onUpdate={updates => onUpdateBlock(index, updates)}
          onRemove={() => onRemoveBlock(index)}
          onMove={direction => onMoveBlock(index, direction)}
        />
      ))
    )}
  </div>
);

interface EmptyBlocksPlaceholderProps {
  onAddBlock: () => void;
}

const EmptyBlocksPlaceholder: React.FC<EmptyBlocksPlaceholderProps> = ({ onAddBlock }) => (
  <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
    <Type size={48} className="mx-auto text-gray-400 mb-4" />
    <h4 className="text-lg font-medium text-gray-900 mb-2">No text blocks yet</h4>
    <p className="text-gray-500 mb-4">Add your first text block to get started</p>
    <button
      type="button"
      onClick={onAddBlock}
      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
    >
      <Plus size={16} className="mr-2" />
      Add First Block
    </button>
  </div>
);

interface BlockItemProps {
  block: TextBlock;
  index: number;
  totalBlocks: number;
  onUpdate: (updates: Partial<TextBlock>) => void;
  onRemove: () => void;
  onMove: (direction: 'up' | 'down') => void;
}

const BlockItem: React.FC<BlockItemProps> = ({
  block,
  index,
  totalBlocks,
  onUpdate,
  onRemove,
  onMove,
}) => (
  <div className="border border-gray-200 rounded-lg p-4 mb-4">
    <div className="flex justify-between items-start mb-4">
      <h4 className="font-medium text-gray-900">Block {index + 1}</h4>
      <div className="flex items-center space-x-2">
        <button
          type="button"
          onClick={() => onMove('up')}
          disabled={index === 0}
          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronUp size={16} />
        </button>
        <button
          type="button"
          onClick={() => onMove('down')}
          disabled={index === totalBlocks - 1}
          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronDown size={16} />
        </button>
        <button type="button" onClick={onRemove} className="p-1 text-red-600 hover:text-red-700">
          <Trash2 size={16} />
        </button>
      </div>
    </div>

    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Block Title (optional)
        </label>
        <input
          type="text"
          value={block.title || ''}
          onChange={e => onUpdate({ title: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Optional block title"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Content <span className="text-red-500">*</span>
        </label>
        <RichTextEditor
          content={block.content || ''}
          onChange={html => onUpdate({ content: html })}
          placeholder="Enter block content..."
          minHeight="200px"
        />
        <p className="text-xs text-gray-500 mt-1">Use the toolbar to format your text</p>
      </div>
    </div>
  </div>
);
